// ─────────────────────────────────────────────────────────────
//  Utilitare pentru flotă: statistici, urgență, sortare.
//  Lucrează pe datele reale venite din /masini (PostgreSQL via LeaseinDAC).
//
//  v9.0:
//  • CASCO scos complet — e o chestiune internă LEASEIN, clientul vede
//    doar RCA, ITP și Rovinietă.
//  • Statisticile numără TOATE mașinile la fiecare tip de document:
//    o mașină fără RCA încărcat apare ca „necunoscut" (galben), iar bara
//    arată corect 9/16, nu 9/9.
// ─────────────────────────────────────────────────────────────
import { statusExpirare } from "./expirare";

export const ETICHETE = {
  rca: "RCA",
  itp: "ITP",
  rovinieta: "Rovinietă",
};

// Ordinea de severitate: rosu (expirat) > galben (curând) > verde > necunoscut.
const RANG = { rosu: 0, galben: 1, verde: 2, necunoscut: 3 };

// Cea mai gravă stare a unei mașini, privind toate documentele ei (RCA/ITP/Rovinietă).
// Întoarce { stare, zile } unde zile = cele mai puține zile rămase
// dintre documentele care încă au sens (folosit la sortarea după urgență).
export function stareMasina(masina) {
  let cea = { stare: "necunoscut", zile: null };
  Object.keys(ETICHETE).forEach((tip) => {
    const s = statusExpirare((masina.expira || {})[tip]);
    if (RANG[s.stare] < RANG[cea.stare]) {
      cea = s;
    } else if (RANG[s.stare] === RANG[cea.stare] && s.zile != null) {
      // aceeași severitate → ținem cea mai apropiată de expirare
      if (cea.zile == null || s.zile < cea.zile) cea = s;
    }
  });
  return cea;
}

// Câte documente are mașina în fiecare stare (doar RCA/ITP/Rovinietă).
export function numaraDocumente(masina) {
  const c = { rosu: 0, galben: 0, verde: 0, necunoscut: 0 };
  Object.keys(ETICHETE).forEach((tip) => {
    c[statusExpirare((masina.expira || {})[tip]).stare] += 1;
  });
  return c;
}

// Statistici la nivel de flotă, pentru dashboard.
// v9.0: numitorul fiecărei bare = TOATE mașinile din flotă. Documentele fără
// dată încărcată intră la „necunoscut" (afișate cu galben), nu dispar din bară.
export function statisticiFlota(masini) {
  const total = masini.length;
  const nrTipuri = Object.keys(ETICHETE).length;
  // Pe documente (toată flota)
  const docs = { rosu: 0, galben: 0, verde: 0, necunoscut: 0 };
  // Pe tip de document (câte verzi/galbene/roșii/necunoscute per RCA, ITP, Rovinietă)
  const peTip = {};
  Object.keys(ETICHETE).forEach((t) => {
    peTip[t] = { rosu: 0, galben: 0, verde: 0, necunoscut: 0 };
  });

  // Pe mașini (după cea mai gravă stare)
  let masiniProbleme = 0;
  let masiniExpirate = 0;

  masini.forEach((m) => {
    Object.keys(ETICHETE).forEach((tip) => {
      const st = statusExpirare((m.expira || {})[tip]).stare;
      docs[st] += 1;
      peTip[tip][st] += 1;
    });
    const sm = stareMasina(m).stare;
    if (sm === "rosu") masiniExpirate += 1;
    if (sm === "rosu" || sm === "galben") masiniProbleme += 1;
  });

  // Total documente = mașini × tipuri (include necunoscutele — de aici 9/16, nu 9/9)
  const docsTotal = total * nrTipuri;
  const conforme = docsTotal > 0 ? Math.round((docs.verde / docsTotal) * 100) : null;

  return {
    total,
    docs,
    docsTotal,
    peTip,
    masiniProbleme,
    masiniExpirate,
    masiniOk: total - masiniProbleme,
    masiniInService: masini.filter((m) => m.statusOperational === "in_service").length,
    conforme, // procent documente valabile din TOATE documentele flotei
  };
}

// Comparatoare pentru sortare.
export const SORTARI = {
  urgenta: (a, b) => {
    const sa = stareMasina(a);
    const sb = stareMasina(b);
    if (RANG[sa.stare] !== RANG[sb.stare]) return RANG[sa.stare] - RANG[sb.stare];
    const za = sa.zile == null ? Infinity : sa.zile;
    const zb = sb.zile == null ? Infinity : sb.zile;
    return za - zb;
  },
  numar: (a, b) =>
    (a.nrInmatriculare || "").localeCompare(b.nrInmatriculare || "", "ro", {
      numeric: true,
    }),
  model: (a, b) => (a.model || "").localeCompare(b.model || "", "ro"),
};

// Filtrează după text (nr înmatriculare, model, șasiu) și după stare.
export function filtreazaMasini(masini, { cautare = "", filtru = "toate" } = {}) {
  const q = cautare.trim().toLowerCase();
  return masini.filter((m) => {
    if (q) {
      const hay = `${m.nrInmatriculare || ""} ${m.model || ""} ${m.serieSasiu || ""}`.toLowerCase();
      if (!hay.includes(q)) return false;
    }
    if (filtru === "toate") return true;
    if (filtru === "service") return m.statusOperational === "in_service";
    const st = stareMasina(m).stare;
    if (filtru === "probleme") return st === "rosu" || st === "galben";
    if (filtru === "expirate") return st === "rosu";
    if (filtru === "ok") return st === "verde";
    return true;
  });
}
